import { signal } from '@preact/signals-react';
import { ANYONE_STAFF } from 'AppointmentBox/helper';
import usePassCodeVerify from 'Turn/TurnModals/PassCodeVerify';
import { Avatar, Empty, Row, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { APPOINTMENT_SOURCE_TYPE } from 'constants/calendar';
import moment from 'utils/moment';
import { useMemo, useState } from 'react';
import aptActions from 'services/actions/apt';
import aptSelectors from 'services/selectors/apt';
import shopSelectors from 'services/selectors/shop';
import { IAppointmentResItem, IAppointmentServiceResItem } from 'services/types/response';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { formatPhone } from 'utils/formatPhone';
import StatusCell from './StatusCell';
import { AppointmentStatus } from 'Turn/services/constants';
import { formatCancelledBy, getCancelSourceLabel, hasCancelAudit } from 'utils/cancel-audit';
export const tableLoading = signal<boolean>(false);
const locale = {
  emptyText: <Empty description={'No appointment'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
};

const time = (date: string) => moment(date, 'MM/DD/YYYY hh:mm A');

const isCancelled = (record: IAppointmentResItem) => record.status === AppointmentStatus.CANCEL;

/**
 * Cot thong tin huy. Chi hien khi bat "Show cancelled"; o cua lich chua huy de trong.
 * Lich huy truoc khi BE ghi thong tin huy chi co Source "Unknown", cac o khac trong.
 */
const cancelAuditColumns: ColumnsType<IAppointmentResItem> = [
  {
    title: 'CANCELLED BY',
    key: 'cancelledBy',
    width: 140,
    render: (_, record) => isCancelled(record) && hasCancelAudit(record) ? (
      <p>{formatCancelledBy(record) || '--'}</p>
    ) : null,
  },
  {
    title: 'SOURCE',
    key: 'cancelSource',
    width: 120,
    render: (_, record) => isCancelled(record) ? (
      <p>{getCancelSourceLabel(record.cancelSource)}</p>
    ) : null,
  },
  {
    title: 'REASON',
    key: 'cancelReason',
    width: 200,
    render: (_, record) => isCancelled(record) && record.cancelReason ? (
      <Tooltip title={record.cancelReason}>
        <ReasonText>{record.cancelReason}</ReasonText>
      </Tooltip>
    ) : null,
  },
  {
    title: 'CANCELLED AT',
    key: 'cancelledAt',
    width: 170,
    render: (_, record) => isCancelled(record) && record.cancelledAt ? (
      <p>{time(record.cancelledAt).format('MM-DD-YYYY hh:mm A')}</p>
    ) : null,
  },
];

const AptListTable = () => {
  const staffs = shopSelectors.staff();
  const includeCancelled = !!aptSelectors.table.params().includeCancelled;
  const [isMaskPhone, setMaskPhone] = useState(true);
  const [verifyRef, pinCodeContext] = usePassCodeVerify();

  const handleToggleVerify = () => {
    const nextToggle = !isMaskPhone;
    if (!nextToggle) return verifyRef.current?.checkAdmin(async () => setMaskPhone(false), {
      title: 'PIN CODE',
      placeholder: 'Enter your Pin Code',
    });
    setMaskPhone(true);
  };

  const columns: ColumnsType<IAppointmentResItem> = useMemo(() => [
    {
      title: 'DATE',
      dataIndex: 'start_time',
      width: 110,
      render: (start_time: string) => <p>{start_time ? time(start_time)?.format('MM-DD-YYYY') : '--'}</p>,
    },
    {
      title: 'TIME',
      dataIndex: 'start_time',
      width: 170,
      render: (start_time: string, record) => <p>{time(record.start_time).format('hh:mm A')} - {time(record.end_time).format('hh:mm A')}</p>,
    },
    {
      title: 'STAFF',
      dataIndex: 'staffId',
      width: 160,
      render: (staffId: string) => {
        const { avatar, name } = staffs.find(o => o.id.toString() === staffId.toString()) || ANYONE_STAFF;
        return (
          <StaffCell>
            <Avatar size={28} src={avatar} >{name?.[0] || ''}</Avatar>
            <p>{name}</p>
          </StaffCell>
        );
      },
    },
    {
      title: 'CUSTOMER',
      dataIndex: 'customerName',
      width: 160,
      render: (customerName: string) => <p>{customerName}</p>
    },
    {
      title: (
        <Row align={'middle'} wrap={false}>
          <p>CUSTOMER PHONE</p>
          <PhoneToggle type='button' onClick={handleToggleVerify} aria-label={isMaskPhone ? 'Show phone numbers' : 'Hide phone numbers'}>
            {isMaskPhone ? <EyeOpen /> : <EyeClose />}
          </PhoneToggle>
        </Row>
      ),
      dataIndex: 'customerPhone',
      width: 180,
      render: (customerPhone: string) => <p>{formatPhone(customerPhone)}</p>
    },
    {
      title: 'SERVICES',
      dataIndex: 'services',
      width: 220,
      render: (services: IAppointmentServiceResItem[]) => {
        return (
          <>
            {services.map(o => (<p key={o.id}>{o.serviceName} ({o.duration}m)</p>))}
          </>
        );
      }
    },
    {
      title: 'STATUS',
      align: 'center',
      dataIndex: 'status',
      width: 140,
      render: (status: number, record) => (
        <StatusCell
          appointmentId={record.id}
          status={status}
          verifyRef={verifyRef}
          disabled={tableLoading.value}
        />
      ),
    },
    {
      title: 'TYPE',
      align: 'center',
      dataIndex: 'bookingType',
      width: 130,
      render: (bookingType: number) => {
        return (
          <Row align={'middle'} justify={'center'} wrap={false} className='type-cell'>
            {bookingType !== APPOINTMENT_SOURCE_TYPE.CALl_IN ? <IconOnline /> : <IconCalling />}
            <SourceTypeText>{bookingType !== APPOINTMENT_SOURCE_TYPE.CALl_IN ? 'WEBSITE' : 'CALL-IN'}</SourceTypeText>
          </Row>
        );
      }
    },
    ...(includeCancelled ? cancelAuditColumns : []),
  ] as ColumnsType<IAppointmentResItem>, [isMaskPhone, staffs, tableLoading.value, includeCancelled]);

  const dispatch = useAppDispatch();

  const appointments = aptSelectors.table.data();
  const params = aptSelectors.table.params();
  const total = aptSelectors.table.total();


  const handlePageChange = async (page?: number) => {
    dispatch(aptActions.table.setParams({ page: page || 1 }));
  };

  return (
    <>
      <AptListTableStyled>
        <Table
          rowKey={'id'}
          columns={columns}
          dataSource={appointments}
          locale={locale}
          loading={tableLoading.value}
          className='appointment-list-table'
          // Cot huy lam bang rong hon man hinh: cuon ngang thay vi cat chu o moi cot.
          scroll={{ x: 'max-content' }}
          tableLayout='fixed'
          pagination={{
            total,
            pageSize: params.pageSize,
            current: params.page,
            showSizeChanger: false,
            onChange: handlePageChange,
          }}

        />
      </AptListTableStyled>
      {pinCodeContext}
    </>
  );
};

export default AptListTable;

const AptListTableStyled = styled.div`
display: flex;
flex-direction: column;
gap: 8px;
.ant-table-wrapper.appointment-list-table {
  .ant-table-container .ant-table-content .ant-table-cell {
    padding: 8px 12px;
  }
  /* Ngay, gio, so dien thoai: mot dong, so thang cot de quet doc nhanh. */
  .ant-table-tbody > tr > td {
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
  .type-cell svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
}
`;

const StaffCell = styled.div`
display: flex;
align-items: center;
gap: 8px;
min-width: 0;
`;

/* Nut an/hien so dien thoai nam trong header toi mau. */
const PhoneToggle = styled.button`
display: inline-flex;
align-items: center;
justify-content: center;
width: 24px;
height: 24px;
margin-left: 8px;
padding: 0;
border: none;
border-radius: 4px;
background: #FFFFFF;
cursor: pointer;
transition: background-color 0.15s ease;
svg {
  width: 16px;
  height: 16px;
}
&:hover {
  background: #E5E6EB;
}
&:focus-visible {
  outline: 2px solid #148CF1;
  outline-offset: 2px;
}
`;

const ReasonText = styled.p`
max-width: 100%;
overflow: hidden;
text-overflow: ellipsis;
white-space: nowrap;
`;

const SourceTypeText = styled.span`
color: #1D2129;
font-family: Poppins;
font-size: 13px;
font-style: normal;
font-weight: 600;
line-height: normal;
margin-left: 6px;
white-space: nowrap;
`;

const EyeOpen = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12.3521 15.8408C10.2323 15.8408 8.51172 14.1202 8.51172 12.0005C8.51172 9.88077 10.2323 8.16016 12.3521 8.16016C14.4718 8.16016 16.1924 9.88077 16.1924 12.0005C16.1924 14.1202 14.4718 15.8408 12.3521 15.8408ZM12.3521 9.49053C10.9685 9.49053 9.84209 10.6169 9.84209 12.0005C9.84209 13.3841 10.9685 14.5105 12.3521 14.5105C13.7356 14.5105 14.862 13.3841 14.862 12.0005C14.862 10.6169 13.7356 9.49053 12.3521 9.49053Z" fill="#1D313B" />
    <path d="M12.3526 20C9.01773 20 5.86918 18.0488 3.7051 14.6608C2.76497 13.1973 2.76497 10.8115 3.7051 9.33924C5.87805 5.95122 9.0266 4 12.3526 4C15.6785 4 18.8271 5.95122 20.9911 9.33924C21.9313 10.8027 21.9313 13.1885 20.9911 14.6608C18.8271 18.0488 15.6785 20 12.3526 20ZM12.3526 5.33038C9.4878 5.33038 6.74722 7.05099 4.83148 10.0576C4.1663 11.0954 4.1663 12.9047 4.83148 13.9424C6.74722 16.949 9.4878 18.6696 12.3526 18.6696C15.2173 18.6696 17.9579 16.949 19.8736 13.9424C20.5388 12.9047 20.5388 11.0954 19.8736 10.0576C17.9579 7.05099 15.2173 5.33038 12.3526 5.33038Z" fill="#1D313B" />
  </svg>
);

const EyeClose = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6.11703 10.4412C5.97562 10.4412 5.83421 10.3891 5.72257 10.2775C5.11227 9.66719 4.77734 8.85593 4.77734 8.00002C4.77734 6.22123 6.22123 4.77734 8.00002 4.77734C8.85593 4.77734 9.66719 5.11227 10.2775 5.72257C10.3817 5.82677 10.4412 5.96818 10.4412 6.11703C10.4412 6.26588 10.3817 6.4073 10.2775 6.51149L6.51149 10.2775C6.39985 10.3891 6.25844 10.4412 6.11703 10.4412ZM8.00002 5.89375C6.83896 5.89375 5.89375 6.83896 5.89375 8.00002C5.89375 8.37216 5.9905 8.72941 6.16913 9.042L9.042 6.16913C8.72941 5.9905 8.37216 5.89375 8.00002 5.89375Z" fill="#1D313B" />
    <path d="M3.23576 12.8437C3.10924 12.8437 2.97527 12.799 2.87107 12.7097C2.0747 12.0324 1.3602 11.1989 0.749898 10.2313C-0.0390286 9.00326 -0.0390286 7.00117 0.749898 5.76567C2.56592 2.92255 5.20808 1.28516 7.99906 1.28516C9.63646 1.28516 11.2515 1.8508 12.6656 2.91511C12.9112 3.10118 12.9633 3.45098 12.7773 3.69659C12.5912 3.9422 12.2414 3.9943 11.9958 3.80823C10.7752 2.88534 9.39085 2.40156 7.99906 2.40156C5.5951 2.40156 3.2953 3.84545 1.68768 6.36852C1.12948 7.23934 1.12948 8.75765 1.68768 9.62844C2.24588 10.4992 2.88595 11.251 3.59301 11.8613C3.82374 12.0622 3.85351 12.412 3.65255 12.6502C3.54836 12.7767 3.39206 12.8437 3.23576 12.8437Z" fill="#1D313B" />
    <path d="M8.00049 14.7107C7.01062 14.7107 6.04303 14.5098 5.11269 14.1153C4.82987 13.9962 4.6959 13.6687 4.81498 13.3859C4.93406 13.1031 5.26154 12.9691 5.54437 13.0882C6.33329 13.4231 7.15947 13.5943 7.99305 13.5943C10.397 13.5943 12.6968 12.1504 14.3045 9.62736C14.8627 8.75656 14.8627 7.23825 14.3045 6.36746C14.0737 6.00276 13.8206 5.65296 13.5528 5.32548C13.3592 5.08731 13.3965 4.7375 13.6346 4.53655C13.8728 4.34304 14.2225 4.37281 14.4235 4.61842C14.7138 4.97567 14.9966 5.36269 15.2497 5.7646C16.0386 6.99264 16.0386 8.99473 15.2497 10.2302C13.4337 13.0733 10.7915 14.7107 8.00049 14.7107Z" fill="#1D313B" />
    <path d="M8.51239 11.1776C8.25189 11.1776 8.01372 10.9915 7.96163 10.7236C7.90208 10.4184 8.10304 10.1281 8.40819 10.076C9.22688 9.92719 9.91161 9.24247 10.0605 8.42377C10.12 8.11862 10.4103 7.92511 10.7154 7.97721C11.0206 8.03675 11.2215 8.32701 11.162 8.63216C10.9238 9.91975 9.89673 10.9394 8.61658 11.1776C8.57937 11.1701 8.5496 11.1776 8.51239 11.1776Z" fill="#1D313B" />
    <path d="M0.556342 15.9985C0.414931 15.9985 0.273519 15.9464 0.161879 15.8347C-0.0539596 15.6189 -0.0539596 15.2616 0.161879 15.0458L5.72158 9.4861C5.93742 9.27026 6.29467 9.27026 6.51051 9.4861C6.72638 9.70194 6.72638 10.0592 6.51051 10.275L0.950805 15.8347C0.839165 15.9464 0.697754 15.9985 0.556342 15.9985Z" fill="#1D313B" />
    <path d="M9.88251 6.67422C9.7411 6.67422 9.59969 6.62212 9.48805 6.51051C9.27221 6.29467 9.27221 5.93742 9.48805 5.72158L15.0478 0.161879C15.2636 -0.0539596 15.6208 -0.0539596 15.8367 0.161879C16.0525 0.377717 16.0525 0.734967 15.8367 0.950806L10.277 6.51051C10.1653 6.62212 10.0239 6.67422 9.88251 6.67422Z" fill="#1D313B" />
  </svg>
);


const IconCalling = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect width="24" height="24" rx="5" transform="matrix(1.19249e-08 -1 -1 -1.19249e-08 24 24)" fill="#1D2129" />
    <path d="M20.6777 10.6892C20.8949 10.5335 21.0219 10.2607 20.9969 9.99383C20.9045 9.16929 20.684 8.3591 20.3428 7.60265C19.5458 5.8273 18.0594 4.37294 16.2657 3.61723C15.5725 3.31428 14.8319 3.13612 14.0839 3.03379L13.8992 3.03379C13.5749 3.06913 13.2837 3.31907 13.2182 3.64189C13.142 3.94852 13.28 4.28975 13.5428 4.46276C13.7556 4.61478 14.0287 4.60632 14.2757 4.65233C15.4713 4.86251 16.6051 5.42423 17.4878 6.25835C18.5531 7.24707 19.2506 8.62156 19.4218 10.0649C19.4457 10.3329 19.5804 10.5994 19.8215 10.7326C20.0818 10.8898 20.4355 10.8732 20.6777 10.6892Z" fill="white" />
    <path d="M19.875 19.8616C20.1003 19.4961 20.214 19.0684 20.2169 18.6403C20.218 17.8669 20.218 17.0931 20.2169 16.3194C20.2147 15.9432 20.1371 15.5655 19.9659 15.2291C19.639 14.5555 18.9687 14.0685 18.2307 13.951C17.6016 13.8594 16.9766 13.7272 16.3766 13.5137C16.1583 13.4405 15.9411 13.3573 15.7125 13.3212C15.2469 13.2413 14.758 13.3076 14.3303 13.5082C13.8319 13.725 13.4917 14.1649 13.1038 14.5264C11.6546 13.6157 10.4218 12.37 9.49896 10.9297C9.80633 10.6087 10.159 10.3219 10.392 9.93801C10.7682 9.34721 10.8411 8.58303 10.6088 7.9245C10.4071 7.34916 10.2285 6.76277 10.1369 6.15872C10.0872 5.82485 10.0364 5.48326 9.8862 5.177C9.52915 4.38448 8.70019 3.84005 7.83221 3.8165C7.0095 3.81245 6.18643 3.8165 5.36372 3.81429C4.71292 3.80877 4.0658 4.0911 3.62629 4.5711C3.26813 4.95209 3.04911 5.45712 3.00825 5.97761C2.98359 6.21246 3.01966 6.44768 3.05132 6.67995C3.45844 10.037 4.97096 13.2329 7.24177 15.7304C8.86215 17.5113 10.8955 18.8987 13.1089 19.8359C14.3229 20.3302 15.5914 20.6928 16.8886 20.8872C17.2799 20.9317 17.6704 21.0315 18.0669 20.9902C18.8034 20.9302 19.4969 20.4966 19.875 19.8616ZM18.6378 18.7776C18.5833 19.1379 18.245 19.4298 17.8806 19.4317C14.4532 19.1008 11.1797 17.5025 8.76387 15.0561C6.53539 12.775 5.03722 9.77457 4.62274 6.60743C4.59255 6.36522 4.51746 6.10645 4.62384 5.87197C4.73758 5.57602 5.04936 5.37282 5.36556 5.38387C6.16324 5.3824 6.96054 5.38497 7.75822 5.3824C8.06668 5.39123 8.37294 5.58596 8.47969 5.88117C8.55368 6.09062 8.5662 6.31516 8.60706 6.53197C8.69025 7.01308 8.81246 7.48646 8.96043 7.95137C9.02338 8.14204 9.09553 8.33014 9.14853 8.52376C9.22215 8.79211 9.12608 9.09211 8.92546 9.28168C8.6019 9.60782 8.27429 9.92991 7.95221 10.2575C7.72214 10.4872 7.66104 10.8616 7.80312 11.1531C8.66006 12.6608 9.80559 14.0066 11.1705 15.0778C11.4414 15.3034 11.7381 15.4949 12.0219 15.7036C12.266 15.8674 12.5225 16.0117 12.771 16.1688C12.9919 16.3153 13.2863 16.3474 13.5297 16.2406C13.6865 16.1762 13.8039 16.0499 13.9206 15.9322C14.2033 15.6506 14.4805 15.3638 14.7646 15.0837C14.9189 14.9398 15.1283 14.8474 15.3415 14.8606C15.5123 14.8614 15.6691 14.9364 15.8292 14.985C16.4701 15.2088 17.1319 15.376 17.8044 15.4706C17.9782 15.5007 18.1618 15.5284 18.3069 15.6366C18.5277 15.786 18.6536 16.0536 18.6444 16.3179C18.6396 17.1377 18.6529 17.9582 18.6378 18.7776Z" fill="white" />
    <path d="M17.8358 10.2414C17.8652 10.1222 17.8663 9.99739 17.8394 9.87776C17.6528 8.96082 17.1912 8.1002 16.5202 7.44683C15.858 6.79087 14.9933 6.34584 14.0767 6.17872C13.871 6.15369 13.6527 6.20706 13.4903 6.33811C13.2897 6.49418 13.1734 6.75222 13.1925 7.00584C13.2087 7.28817 13.3906 7.55542 13.6505 7.66805C13.826 7.74462 14.0204 7.75787 14.1993 7.8245C15.2263 8.1499 16.0512 9.04585 16.2776 10.1008C16.3174 10.304 16.3965 10.5102 16.5596 10.6464C16.7742 10.8348 17.0959 10.8945 17.3613 10.7859C17.5965 10.6968 17.7791 10.4859 17.8358 10.2414Z" fill="white" />
  </svg>
);

const IconOnline = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect width="24" height="24" rx="5" fill="#F05326" />
    <path d="M11.8481 3H12.1174C13.4741 3.02215 14.8287 3.33996 16.0402 3.95484C18.0205 4.94836 19.612 6.69633 20.4027 8.76773C20.7827 9.74859 20.9761 10.7973 21 11.8481V12.1181C20.9771 13.6045 20.5936 15.0871 19.8627 16.3836C19.1849 17.5955 18.223 18.647 17.0773 19.4317C15.6299 20.434 13.8787 20.9757 12.1206 21H11.8541C10.232 20.9705 8.61551 20.5078 7.24195 19.6405C6.20836 18.995 5.30766 18.1383 4.61121 17.1384C3.58184 15.6795 3.02461 13.9041 3 12.1209V11.8548C3.03023 10.0703 3.59238 8.29523 4.62738 6.83836C5.48414 5.61633 6.64781 4.6098 7.98375 3.94289C9.17871 3.34066 10.5118 3.02813 11.8481 3ZM10.2499 4.90582C9.53062 5.64586 9.08062 6.59684 8.73187 7.55625C9.17695 7.59598 9.62133 7.63957 10.0661 7.67789C10.5343 7.70074 11.003 7.71867 11.4716 7.73941C11.4716 6.53637 11.4716 5.33332 11.4716 4.13027C11.0019 4.2709 10.5899 4.55812 10.2499 4.90582ZM12.5273 4.12992C12.5273 5.33402 12.5273 6.53848 12.5273 7.74293C12.9946 7.725 13.4621 7.70988 13.9294 7.6902C14.3783 7.65504 14.8269 7.61637 15.2755 7.57875C15.0259 6.89039 14.7316 6.21152 14.3161 5.60578C13.8795 4.95926 13.2927 4.36055 12.5273 4.12992ZM5.82727 6.99902C6.1159 7.07145 6.40418 7.14668 6.69352 7.21699C7.01801 7.28379 7.34391 7.3432 7.6691 7.40613C7.90324 6.70195 8.19047 6.01113 8.58246 5.37867C8.73223 5.09918 8.93438 4.85309 9.11508 4.59363C7.83469 5.09285 6.69492 5.93449 5.82727 6.99902ZM14.8849 4.59363C15.0663 4.85414 15.2692 5.10129 15.4196 5.38219C15.8166 6.02344 16.1048 6.7248 16.3425 7.43848C16.6628 7.37941 16.9837 7.32387 17.304 7.26305C17.6071 7.19484 17.9087 7.11891 18.2111 7.04719C17.3395 5.95945 16.1843 5.10023 14.8849 4.59363ZM5.18777 7.90781C4.53984 8.9857 4.15699 10.2179 4.07367 11.4727C5.04715 11.4727 6.02098 11.473 6.9948 11.4723C7.03066 10.4482 7.15441 9.42727 7.38293 8.42812C6.64184 8.2977 5.90602 8.13281 5.18777 7.90781ZM16.6245 8.46117C16.8473 9.44977 16.9704 10.4588 17.0052 11.4713C17.979 11.472 18.9529 11.4716 19.9263 11.4713C19.8441 10.239 19.4735 9.02789 18.8456 7.96371C18.1172 8.17852 17.3733 8.33707 16.6245 8.46117ZM8.05055 11.4713C9.19102 11.4716 10.3311 11.4716 11.4716 11.4716C11.4716 10.5793 11.472 9.68742 11.4713 8.79551C10.4556 8.77195 9.44062 8.70621 8.43199 8.58246C8.20699 9.52957 8.08781 10.4992 8.05055 11.4713ZM12.5288 8.79867C12.528 9.68988 12.5284 10.5807 12.5284 11.4716C13.6689 11.4716 14.809 11.4716 15.9495 11.4713C15.915 10.5073 15.793 9.5468 15.575 8.60672C14.5636 8.72062 13.5465 8.7825 12.5288 8.79867ZM4.07367 12.5273C4.15559 13.7441 4.51629 14.9405 5.13047 15.9945C5.86664 15.7733 6.62004 15.6148 7.37766 15.4868C7.15723 14.5151 7.03559 13.523 6.99691 12.5277C6.02238 12.527 5.04785 12.5273 4.07367 12.5273ZM8.05266 12.5288C8.09238 13.4752 8.20945 14.4184 8.42812 15.3405C9.43887 15.2266 10.4549 15.1644 11.4716 15.1482C11.4741 14.275 11.472 13.4017 11.4727 12.5284C10.3325 12.5284 9.19277 12.528 8.05266 12.5288ZM12.5284 12.5284C12.5288 13.4027 12.528 14.2767 12.5288 15.1511C13.5444 15.1714 14.5587 15.2404 15.567 15.3616C15.787 14.4325 15.9087 13.4822 15.9473 12.5288C14.8076 12.5284 13.6682 12.5284 12.5284 12.5284ZM17.0031 12.5288C16.9669 13.5346 16.8361 14.5355 16.6181 15.5177C17.3694 15.6457 18.114 15.8148 18.8414 16.0426C19.4725 14.9774 19.8437 13.7634 19.9263 12.5288C18.9521 12.5284 17.9776 12.528 17.0031 12.5288ZM10.4876 16.2395C9.89906 16.2532 9.31441 16.3253 8.72766 16.3692C9.02648 17.1936 9.39563 18.0068 9.95004 18.6934C10.3448 19.1905 10.8543 19.6233 11.4716 19.8086C11.4716 18.6069 11.4716 17.4049 11.4713 16.2033C11.1432 16.2156 10.8156 16.2275 10.4876 16.2395ZM12.5288 16.2068C12.528 17.4074 12.5284 18.608 12.5284 19.8082C13.2716 19.5853 13.8454 19.0119 14.2774 18.39C14.7063 17.7772 15.0108 17.0885 15.266 16.3882C14.822 16.3478 14.3787 16.3056 13.9346 16.2673C13.466 16.2448 12.9974 16.2272 12.5288 16.2068ZM6.65906 16.6948C6.35742 16.7651 6.05684 16.8396 5.7559 16.912C6.64852 18.0388 7.84242 18.9251 9.18785 19.4341C8.48613 18.577 8.00449 17.5607 7.66453 16.512C7.32844 16.5686 6.99375 16.6318 6.65906 16.6948ZM16.3302 16.5373C15.986 17.5758 15.5093 18.5841 14.8121 19.4341C16.1404 18.9311 17.3223 18.0616 18.21 16.9539C17.9164 16.8793 17.6236 16.8034 17.3297 16.7306C16.9971 16.6628 16.6631 16.6027 16.3302 16.5373Z" fill="white" />
  </svg>
);
